import { Component, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  EChannelMetaKey,
  IChannel,
  IChannelIntegrationMapping,
  IChannelIntegrationMappingOptions,
  IChannelMetaKeyValue,
} from 'v2/modules/shared/interfaces';
import { trackById, trackByKey } from 'v2/modules/shared/util/track-by.util';

import { ChannelFacadeService } from '../../channel-facade.service';

import { IOmsShippingMethod } from './../../../shared/interfaces/oms-shipping-method.interface';
import { AppIntegrationAccountShippingMethodApiService } from './../../../shared/services/api/app-integration/app-integration-account-shipping-method-api.service';

@Component({
  selector: 'lum-channel-details',
  templateUrl: './channel-details.component.html',
  styleUrls: ['./channel-details.component.scss'],
})
export class ChannelDetailsComponent implements OnInit {
  trackById = trackById;
  trackByKey = trackByKey;
  currentShippingMethod :IOmsShippingMethod | null = null;
  currentChannelId :number | null = null;
  pushStockIntegrationMapping :IChannelIntegrationMapping | null = null;
  pushOrderIntegrationMapping :IChannelIntegrationMapping | null = null;
  channelMetafields :IChannelMetaKeyValue | null = null;

  eChannelMetaKey = EChannelMetaKey;

  private onDestroy$ = new Subject();

  constructor(
    private route :ActivatedRoute,
    private channelFacadeService :ChannelFacadeService,
    private snackBar :MatSnackBar,
    private appIntegrationAccountShippingMethodApiService :AppIntegrationAccountShippingMethodApiService,
  ) {}

  ngOnInit() :void {
    this.channelFacadeService.updateState({ currentChannel: null });
    this.initCurrentChannel();
    this.getCurrentChannel();
  }

  private initCurrentChannel() :void {
    const currentChannelId = this.route.snapshot.params.id;
    if (currentChannelId) {
      this.currentChannelId = Number(currentChannelId);

      const params :{ [param :string] :string | string[] } = {
        relations: [
          'pullOrderIntegrationMapping',
          'pullOrderIntegrationMapping.invoiceType',
          'pullOrderIntegrationMapping.appIntegrationAccount',
          'pullOrderIntegrationMapping.store',
          'pushOrderIntegrationMapping.appIntegrationAccount',
          'pushOrderIntegrationMapping.store',
          'tags',
          'getProductsIntegrationMapping',
          'warehouseGroup',
          'pushStockAppIntegrationAccount',
          'pushStockIntegrationMapping',
          'metafields',
        ].join(','),
      };

      this.channelFacadeService
        .getById({
          id: Number(currentChannelId),
          params,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (currentChannel) => {
            if (
              currentChannel?.pushOrderIntegrationMapping?.options
                ?.omsShippingMethodId
            ) {
              this.getShipmentMethods({
                appIntegrationAccountId:
                  currentChannel.pushOrderIntegrationMapping
                    .appIntegrationAccountId,
                id: currentChannel.pushOrderIntegrationMapping.options
                  .omsShippingMethodId,
              });
            }
          },
          error: (error) => {
            this.snackBar.open(
              error?.message || 'Failed to fetch channel.',
              'Close',
              {
                panelClass: 'error',
              },
            );
          },
        });
    }
  }

  private getShipmentMethods(data :{
    appIntegrationAccountId :number;
    id :number;
  }) :void {
    const { appIntegrationAccountId, id } = data;
    this.appIntegrationAccountShippingMethodApiService
      .getById(appIntegrationAccountId, id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (shippingMethod) => {
          this.currentShippingMethod = shippingMethod;
        },
        error: (error) => {
          this.snackBar.open(
            error?.message || 'Failed to fetch shipment methods.',
            'Close',
            {
              panelClass: 'error',
            },
          );
        },
      });
  }

  private getCurrentChannel() :void {
    this.currentChannel$.pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (currentChannel) => {
        this.pushStockIntegrationMapping =
          currentChannel?.pushStockIntegrationMapping || null;
        this.pushOrderIntegrationMapping =
          currentChannel?.pushOrderIntegrationMapping || null;
        this.pushStockIntegrationMapping =
          currentChannel?.pushStockIntegrationMapping || null;
        this.channelMetafields =
          currentChannel?.metafields?.reduce((acc, metafield) => {
            acc[metafield.metaKey] = metafield.metaValue;

            return acc;
          }, {} as IChannelMetaKeyValue) || null;
      },
    });
  }

  get currentChannel$() :Observable<IChannel | null> {
    return this.channelFacadeService.currentChannel$;
  }

  get loading$() :Observable<boolean> {
    return this.channelFacadeService.loading$;
  }

  get hasPushStocksToOms() :boolean {
    return !!this.pushStockIntegrationMapping;
  }

  get pushStocksToOmsOptions() :IChannelIntegrationMappingOptions | null {
    return this.pushStockIntegrationMapping?.options || null;
  }

  get pushOrderToOmsOptions() :IChannelIntegrationMappingOptions | null {
    return this.pushOrderIntegrationMapping?.options || null;
  }
}
